import {useCallback, useContext} from 'react';
import {Alert, Dimensions, Platform} from 'react-native';
import {useTranslation} from 'react-i18next';
import {CartContext} from './CartContext';
import {TableContext} from './TableContext';
import {OnlineOrderContext} from './OnlineOrderContext';
import useConfirmNavigation from './UseConfirmNavigation';
import {planPosModeSwitch} from './posModeSwitch';
import {POS_TABLET_BREAKPOINT} from './posLayout';
import {haptics} from './haptics';

/**
 * Asks a yes/no question with the platform's native dialog.
 *
 * @param {string} title Dialog title (ignored on web).
 * @param {string} message Question.
 * @param {function(): void} onYes Called when confirmed.
 * @param {{no: string, yes: string}} labels Button labels.
 */
const confirmYesNo = (title, message, onYes, labels) => {
    haptics.warning();

    if (Platform.OS === 'web') {
        if (window.confirm(message)) {
            onYes();
        }

        return;
    }

    Alert.alert(title, message, [
        {text: labels.no, style: 'cancel'},
        {text: labels.yes, onPress: onYes},
    ], {cancelable: false});
};

/**
 * Switches between the POS selling modes (quick receipt, tables, delivery) and the
 * POS Hub with the app's cart / table reset rules. Single implementation shared by
 * the order-type button and the Hub tiles (see {@link planPosModeSwitch}).
 *
 * @param {object} navigation Navigation object of the screen that owns the switcher.
 * @returns {function(('takeAway'|'dineIn'|'delivery'|'hub')): void} `switchMode`.
 */
export const usePosModeSwitch = (navigation) => {
    const {t} = useTranslation();
    const {cartItems, alreadySentCartItems, deleteCart, setAlreadySentCartItems} = useContext(CartContext);
    const {currentTable, removeTable} = useContext(TableContext);
    const {orderData, resetOnlineOrder} = useContext(OnlineOrderContext);
    const confirmNavigation = useConfirmNavigation(navigation);

    return useCallback((mode) => {
        const state = navigation.getState();
        const currentRoute = state.routes[state.index].name;
        const plan = planPosModeSwitch({
            mode,
            currentRoute,
            hasDineInSelection: Boolean(currentTable?._id) || orderData?.orderType === 'dineIn',
            hasItemsInProgress: cartItems.length > 0 || alreadySentCartItems.length > 0,
            hasDeliveryDraft: Boolean(orderData?.userInfo && orderData?.addressInfo),
        });
        const labels = {no: t('no'), yes: t('yes')};

        const resetSellingState = () => {
            deleteCart();
            removeTable();
            setAlreadySentCartItems([]);
            resetOnlineOrder();
        };

        const run = () => {
            const {action} = plan;

            if (action.type === 'goto') {
                if (action.keepState) {
                    navigation.navigate(action.screen);
                } else if (cartItems.length > 0) {
                    confirmNavigation(action.screen);
                } else {
                    resetSellingState();
                    navigation.navigate(action.screen);
                }
            } else if (action.type === 'confirmQuickReceipt') {
                confirmYesNo(t('attention'), t(action.messageKey), () => {
                    resetSellingState();
                    // Sales tab is 'BigSale' at >=POS_TABLET_BREAKPOINT, 'Sale' below — target the registered one (SVC-002).
                    const salesTab = Dimensions.get('window').width >= POS_TABLET_BREAKPOINT ? 'BigSale' : 'Sale';
                    navigation.navigate('HomeScreen', {screen: salesTab});
                }, labels);
            }
        };

        haptics.select();

        if (plan.confirmDeliveryDraft && plan.action.type !== 'none') {
            confirmYesNo(t('attention'), t('home_delivery_alert'), run, {no: t('cancel'), yes: t('yes')});
        } else {
            run();
        }
    }, [navigation, currentTable?._id, orderData?.orderType, orderData?.addressInfo, orderData?.userInfo, cartItems.length, alreadySentCartItems.length, confirmNavigation, deleteCart, removeTable, setAlreadySentCartItems, resetOnlineOrder, t]);
};

export default usePosModeSwitch;
